import dayjs from 'dayjs'

export const restaurantOverviewSortKeys = [
  'roomNumber',
  'accommodationDate',
  'spaceReservationDetails',
  'subjectName',
  'unitPrice',
  'quantity',
] as const
export type RestaurantOverviewSortKey = (typeof restaurantOverviewSortKeys)[number]

export const restaurantOverviewColumns = [
  'roomNumber',
  'guestName',
  'customerTags',
  'stayCount',
  'planName',
  'mealCondition',
  'accommodationDate',
  'frontMemo',
  'mealNote',
  'spaceReservationDetails',
  'subjectName',
  'nights',
  'unitPrice',
  'quantity',
] as const
export type RestaurantOverviewColumn = (typeof restaurantOverviewColumns)[number]

export const displayDays = ['sameDay', 'nextDay'] as const
export type DisplayDay = (typeof displayDays)[number]

export type RestaurantSettingTab = 'highlight' | 'displaySubject'

export type RestaurantSortOrder = 'asc' | 'desc'

export type DisplayColumnSetting = {
  columnKey: RestaurantOverviewColumn
  displayOrder: number
  isVisible: boolean
  isRequired: boolean
  isFixed: boolean
}

export type RestaurantOverviewQuery = {
  date: string
  subjectIds: string[]
  spaceIds: string[]
  nightIndexes: number[]
  startTimeFrom?: string
  startTimeTo?: string
  sortKey?: RestaurantOverviewSortKey
  sortOrder?: RestaurantSortOrder
}

export type RestaurantCustomerTag = {
  name: string
  backgroundColor: string
  fontColor: string
  borderColor: string
}

/** ダッシュボードと同じく、部屋ごとの人数を表示するための単位 */
export type RestaurantRoomPax = {
  male: number
  female: number
  childA: number
  childB: number
  childC: number
  childD: number
  childE: number
  childF: number
  childOther: number
  roomNumber: string
  displayOrder: number
}

export type RestaurantSpaceReservationDetailView = {
  spaceUsageDetailId: string
  spaceId: string
  spaceName: string
  useDateStart: string
  useDateEnd: string
  isHighlighted: boolean
}

export type RestaurantSubjectLine = {
  subjectId: string
  subjectName: string
  displayDay: DisplayDay
  unitPrice: number | null
  quantity: number
  nights: string
  isHighlighted: boolean
}

export type RestaurantMealNoteView = {
  memo: string
  isHighlighted: boolean
}

export type RestaurantOverviewRow = {
  reservationId: string
  reservationUuid: string
  customerId: string | null
  roomNumber: RestaurantRoomPax[]
  guestName: string | null
  customerTags: RestaurantCustomerTag[]
  stayCount: number | null
  planName: string | null
  mealCondition: string | null
  accommodationDate: string
  frontMemo: string | null
  mealNote: RestaurantMealNoteView | null
  spaceReservationDetails: RestaurantSpaceReservationDetailView[]
  subjects: RestaurantSubjectLine[]
  rowHighlighted: boolean
}

export type RestaurantOverviewSummaryItem = {
  subjectId: string
  subjectName: string
  countsByNight: Record<string, number>
  total: number
}

export type RestaurantOverviewSummary = {
  items: RestaurantOverviewSummaryItem[]
  nightColumns: number[]
  grandTotal: number
}

export type RestaurantFilterOption = {
  subjectId: string
  subjectName: string
}

export type RestaurantSpaceFilterOption = {
  spaceId: string
  spaceName: string
}

export type RestaurantFilterOptions = {
  subjects: RestaurantFilterOption[]
  spaces: RestaurantSpaceFilterOption[]
  nightIndexes: number[]
  timeRange: {
    min: string
    max: string
  }
}

export type RestaurantOverviewSnapshot = {
  businessDate: string
  displayColumns: DisplayColumnSetting[]
  rows: RestaurantOverviewRow[]
  summary: RestaurantOverviewSummary
  filterOptions: RestaurantFilterOptions
}

export type RestaurantMealSpaceCandidateTimeRange = {
  useDateStart: string
  useDateEnd: string
}

export type RestaurantMealSpaceSelection = {
  spaceId: string
  spaceName: string
  useDateStart: string
  useDateEnd: string
}

export type RestaurantMealSpaceCandidate = {
  spaceId: string
  spaceName: string
  availableTimeRanges: RestaurantMealSpaceCandidateTimeRange[]
}

export type RestaurantMealSpaceCandidateResponse = {
  reservationId: string
  selectedSpaceReservationDetails: RestaurantSpaceReservationDetailView[]
  candidateSpaces: RestaurantMealSpaceCandidate[]
}

export type RestaurantMealSpaceCandidateQuery = {
  reservationId: string
  date: string
}

export type RestaurantCancelledSubject = {
  subjectName: string
  quantity: number
}

export type RestaurantCancellationCard = {
  reservationId: string
  reservationUuid: string
  guestName: string
  salesDate: string
  cancelledAt: string
  cancelledSubjects: RestaurantCancelledSubject[]
  isConfirmed: boolean
}

export type RestaurantCancellationHistoryGroup = {
  salesDate: string
  cancellations: RestaurantCancellationCard[]
}

export type RestaurantCancellationHistoryResponse = {
  cancellationHistoriesByDate: RestaurantCancellationHistoryGroup[]
  highlightRules: RestaurantHighlightRules
}

export type RestaurantCancellationConfirmRequest = {
  reservationId: string
  salesDate: string
  cancelledAt: string
  isConfirmed: boolean
}

export type RestaurantColumnSettingUpdateRequest = {
  displayColumns: DisplayColumnSetting[]
}

export type RestaurantHighlightRules = {
  checkinDays: number
  updatedDays: number
}

export type RestaurantDisplaySubject = {
  subjectId: string
  displayDay: DisplayDay
}

export type RestaurantResolvedDisplaySubject = RestaurantDisplaySubject & {
  subjectName: string
}

export type RestaurantDisplaySubjectsResponse = {
  displaySubjects: RestaurantResolvedDisplaySubject[]
}

export type RestaurantHighlightSettingUpdateRequest = RestaurantHighlightRules

export type RestaurantDisplaySubjectOrderUpdateRequest = {
  displaySubjects: RestaurantDisplaySubject[]
}

export type RestaurantMealMemoUpdateRequest = {
  memo: string | null
  reservationId: string
}

export type RestaurantCreateMealSpaceRequest = {
  reservationId: string
  spaceId: string
  useDateStart: string
  useDateEnd: string
}

export type RestaurantUpdateMealSpaceDetailRequest = {
  spaceUsageDetailId: string
  spaceId: string
  useDateStart: string
  useDateEnd: string
}

export type RestaurantDeleteMealSpaceDetailRequest = {
  spaceUsageDetailId: string
}

export const formatMealSpaceTimeRangeLabel = (range: RestaurantMealSpaceCandidateTimeRange): string =>
  `${dayjs(range.useDateStart).format('H:mm')}〜${dayjs(range.useDateEnd).format('H:mm')}`

export const formatMealSpaceSelectionLabel = (spaceName: string, range: RestaurantMealSpaceCandidateTimeRange): string =>
  `${spaceName}・${formatMealSpaceTimeRangeLabel(range)}`

export const getRestaurantOverviewColumnLabelKey = (columnKey: RestaurantOverviewColumn): string => {
  switch (columnKey) {
    case 'roomNumber':
      return 'RestaurantManager.ColumnRoomNumber'
    case 'guestName':
      return 'RestaurantManager.ColumnGuestName'
    case 'customerTags':
      return 'RestaurantManager.ColumnCustomerTags'
    case 'stayCount':
      return 'RestaurantManager.ColumnStayCount'
    case 'planName':
      return 'RestaurantManager.ColumnPlanName'
    case 'mealCondition':
      return 'RestaurantManager.ColumnMealCondition'
    case 'accommodationDate':
      return 'RestaurantManager.ColumnAccommodationDate'
    case 'frontMemo':
      return 'RestaurantManager.ColumnFrontMemo'
    case 'mealNote':
      return 'RestaurantManager.ColumnMealNote'
    case 'spaceReservationDetails':
      return 'RestaurantManager.ColumnMealSpace'
    case 'subjectName':
      return 'RestaurantManager.ColumnSubjectName'
    case 'nights':
      return 'RestaurantManager.ColumnNights'
    case 'unitPrice':
      return 'RestaurantManager.ColumnUnitPrice'
    case 'quantity':
      return 'RestaurantManager.ColumnQuantity'
    default:
      return 'RestaurantManager.DisplaySettings'
  }
}
